import React from "react";
import { Form, FormField } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { z } from "zod";
import { BasicInfoFields } from "./components/BasicInfoFields";
import { AgeGroupFields } from "./components/AgeGroupFields";
import { TimingFields } from "./components/TimingFields";
import { PricingFields } from "./components/PricingFields";
import { AdditionalProgramFields } from "./components/AdditionalProgramFields";
import { ProgramColorField } from "./components/ProgramColorField";
import { useAgeGroupVariationForm } from "./hooks/useAgeGroupVariationForm";
import { useOfferingOptions } from "../hooks/useOfferingOptions";
import { AgeGroupVariationFormData } from "./schemas/ageGroupVariationSchema";

interface AgeGroupVariationFormProps {
  defaultValues?: AgeGroupVariationFormData;
  onSubmit: (data: AgeGroupVariationFormData) => void;
  onDirtyChange: (isDirty: boolean) => void;
  onValuesChange?: (data: AgeGroupVariationFormData) => void;
  existingKeys?: string[];
  isEditMode?: boolean;
  /** Optional schema for validation (e.g. sharedProgramVariationSchema for g- prefix). */
  schema?: z.ZodType<AgeGroupVariationFormData>;
  /** Placeholder for key field (e.g. "g-camp-option1" for shared programs). */
  keyPlaceholder?: string;
}

export const AgeGroupVariationForm: React.FC<AgeGroupVariationFormProps> = ({
  defaultValues,
  onSubmit,
  onDirtyChange,
  onValuesChange,
  existingKeys = [],
  isEditMode = false,
  schema,
  keyPlaceholder
}) => {
  const { form, gradeOptions, loading } = useAgeGroupVariationForm(defaultValues, onDirtyChange, schema);
  const { offeringOptions, loading: loadingOfferings } = useOfferingOptions();

  React.useEffect(() => {
    const subscription = form.watch((values) => {
      onDirtyChange(form.formState.isDirty);
      onValuesChange?.(values as AgeGroupVariationFormData);
    });
    return () => subscription.unsubscribe();
  }, [form, onDirtyChange, onValuesChange]);

  if (loading || loadingOfferings) {
    return <div>Loading...</div>;
  }

  const handleFormSubmit = (data: AgeGroupVariationFormData) => {
    console.log("[AgeGroupVariationForm] Form submitted with data:", data);
    onSubmit(data);
  };

  return (
    <Form {...form}>
      <form 
        id="age-group-variation-form" 
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-6"
      >
        <div className="space-y-6">
          <BasicInfoFields 
            form={form} 
            offeringOptions={offeringOptions || []}
            existingKeys={existingKeys}
            isEditMode={isEditMode}
            keyPlaceholder={keyPlaceholder}
          />

          <Separator />

          <div>
            <h3 className="text-lg font-semibold mb-2 text-left px-1">Age group / eligibility</h3>
            <p className="text-sm text-muted-foreground mb-4 text-left px-1">
              Eligibility requirements can be defined based on years old, grade, or both. (Most camps just use age.)
            </p>
            <AgeGroupFields 
              form={form} 
              gradeOptions={gradeOptions || []} 
            />
            <div className="mt-4 px-1">
              <FormField
                control={form.control}
                name="allowSessionAgeOverride"
                render={({ field }) => (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                    <span>Sessions may override this age range</span>
                  </label>
                )}
              />
            </div>
          </div>

          <Separator />

          <TimingFields form={form} />

          <Separator />

          <PricingFields form={form} />

          <Separator />

          <AdditionalProgramFields form={form} />

          <Separator />

          <ProgramColorField form={form} />
        </div>
      </form>
    </Form>
  );
};
